import * as React from "react";
import { useSelector } from "react-redux";
import { Button, Typography, Box, Grid } from "@mui/material";
import { ModeEdit as ModeEditIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import EditBg from "../../../icons/edit holder.png";
import BalanceDetailsIcon from "../../../icons/RecurringDetailsIcon.svg";
import { selectBalanceDetails } from "../sagas/residentpaymentsSlice";
import { MaskedInputState } from "../../../shared/MaskedInput";
import NumberFormat from "react-number-format";
import ModalContentsPayMethods from "./ModalContentsPayMethods";
import { AmountTier, CreateOneTimePaymentDto, CreateTransactionDetailDto, FeesDTO, ResidentDTO } from "@inosio/payments-sdk";
import { DEFAULT_ADHOC_BANK_UUID, DEFAULT_ADHOC_CARD_UUID, selectResidentBalanceDetails } from "../../../pmui/residents/saga/pmResidentsSlice";
import { selectAdhocBankAccount, selectAdhocBankCard } from "../../../pmui/residents/saga/pmResidentsSlice";
import { selectAuthState } from "../../../shared/sagas/auth/authSlice";
import { createTransDetailRecords, getSumOfPropPayTypesAmount, useIsPmAdhoc } from "./UseSharedMethods";
import { getDate } from "date-fns";
import { selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";

const useStyles = makeStyles({
  totalPaymentSubContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  editBtn: {
    backgroundImage: "url(" + EditBg + ")",
    backgroundRepeat: "no-repeat",
    backgroundSize: "25px 25px",
    width: "25px",
    height: "25px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "11px",
    marginRight: "0",
  },
});

interface PropsType {
  residentsDetails: ResidentDTO;
  maskedInputValues: MaskedInputState;
  setOpenBalanceDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  closeCallback: () => void;
  continueCallback: (val: { createOtpDto: CreateOneTimePaymentDto, lastFour: string }) => void;
}

export default function OtpModalContents(props: PropsType) {
  const { residentsDetails, maskedInputValues, setOpenBalanceDetailsModal, closeCallback, continueCallback } = props;
  const { totalPaymentSubContainer, editBtn } = useStyles();
  const { ipaUser } = useSelector(selectAuthState);
  const adhocBankAccount = useSelector(selectAdhocBankAccount);
  const adhocBankCard = useSelector(selectAdhocBankCard);
  const isPmAdhoc = useIsPmAdhoc();
  const currentBalanceInfo = useSelector(isPmAdhoc ? selectResidentBalanceDetails: selectBalanceDetails);
  const [lastFour, setLastFour] = React.useState('');
  const [createOtpDto, setCreateOtpDto] = React.useState<CreateOneTimePaymentDto>();
  const [disableContinue, setDisableContinue] = React.useState(true);
  const paymentAmountCents = getSumOfPropPayTypesAmount(maskedInputValues);

  const setSelectedPaymentHandler = (feesCents: number, lastFour: string, methodUuid: string, tiers?: AmountTier[], rawFeeDTO?: FeesDTO) => {
    const totalPaymentCents = paymentAmountCents + feesCents;
    setLastFour(lastFour);

    if (isPmAdhoc) {
      // adhoc otp from pmUI
      const adhocOtpRequest = {
        PayerUuid: residentsDetails.Uuid,
        TotalAmount: totalPaymentCents,
        FeeAmount: feesCents,
        BankCard: methodUuid.length && (methodUuid === DEFAULT_ADHOC_CARD_UUID ? adhocBankCard : undefined),
        BankAccount: methodUuid.length && (methodUuid === DEFAULT_ADHOC_BANK_UUID ? adhocBankAccount : undefined),
        TransactionDetails: createTransDetailRecords(maskedInputValues, currentBalanceInfo, methodUuid).map(
          (transDetail) =>
            ({
              ...transDetail,
              FromPayMethodUuid: undefined,
            } as CreateTransactionDetailDto)
        ),
      } as CreateOneTimePaymentDto;

      setCreateOtpDto(adhocOtpRequest);
      return;
    }

    const otpRequest = {
      TotalAmount: totalPaymentCents,
      FeeAmount: feesCents,
      FeePayMethodUuid: (methodUuid === DEFAULT_ADHOC_CARD_UUID || methodUuid === DEFAULT_ADHOC_BANK_UUID) ? undefined : methodUuid,
      BankCard: methodUuid === DEFAULT_ADHOC_CARD_UUID ? adhocBankCard : undefined,
      BankAccount: methodUuid === DEFAULT_ADHOC_BANK_UUID ? adhocBankAccount : undefined,
      PayerUuid: residentsDetails?.Uuid,
      Memo: "One-time payment",
      TransactionDetails: !ipaUser?.currentProfile?.role
        ? createTransDetailRecords(maskedInputValues, currentBalanceInfo, methodUuid)
        : createTransDetailRecords(maskedInputValues, currentBalanceInfo, methodUuid).map(
            (transDetail) =>
              ({
                ...transDetail,
                FromPayMethodUuid: undefined,
              } as CreateTransactionDetailDto)
          ),
    } as CreateOneTimePaymentDto;

    setCreateOtpDto(otpRequest);
  };

  React.useEffect(() => {
    if (createOtpDto) setDisableContinue(false); 
    else setDisableContinue(true);
  }, [createOtpDto]);

  const handleContinue = () => {
    createOtpDto && continueCallback({
      createOtpDto,
      lastFour,
    });
  }

  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const isOTPEnabled = currentBalanceInfo?.PaymentMethodAllowed && propertyGroupDetails.PaymentsEnabled && !!currentBalanceInfo.PropertyPaymentTypes?.length;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              style={{
                color: "#14142B",
                fontSize: "16px",
                lineHeight: "22px",
                fontFamily: "'Work Sans', sans-serif",
                fontWeight: 400,
                textAlign: "center"
              }}
            >
              <NumberFormat
                thousandsGroupStyle="thousand"
                value={(currentBalanceInfo.Balance ?? 0) / 100}
                prefix="Current Balance: $"
                decimalSeparator="."
                displayType="text"
                type="text"
                thousandSeparator={true}
                allowNegative={true}
                decimalScale={2}
                fixedDecimalScale={true}
              />
              <img src={BalanceDetailsIcon} alt="Show Balance Detail" style={{ cursor: "pointer", marginLeft: '5px' }} onClick={() => setOpenBalanceDetailsModal(true)} />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              style={{
                color: "#14142B",
                fontSize: "18px",
                lineHeight: "22px",
                fontWeight: 700,
                fontFamily: "'Open Sans', sans-serif",
              }}
            >
              How much do you want to pay?
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography id="payment-modal-sub-amount">Amount</Typography>
          </Grid>
          <Grid item xs={6}>
            <div className={totalPaymentSubContainer}>
              <div style={{ cursor: "pointer" }}>
                <Typography id="payment-modal-sub-amount">
                  <NumberFormat
                    thousandsGroupStyle="thousand"
                    value={paymentAmountCents / 100}
                    prefix="$"
                    decimalSeparator="."
                    displayType="text"
                    type="text"
                    thousandSeparator={true}
                    allowNegative={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </Typography>
              </div>
              <div className={editBtn}>
                <ModeEditIcon sx={{ color: "#fff" }} style={{ cursor: "pointer" }} onClick={() => setOpenBalanceDetailsModal(true)} />
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography
              style={{
                color: "#14142B",
                fontSize: "18px",
                lineHeight: "22px",
                fontWeight: 700,
                fontFamily: "'Open Sans', sans-serif",
              }}
            >
              Which Payment Method?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ModalContentsPayMethods 
              irpPaymentClass="OTP"
              setSelectedPaymentHandler={setSelectedPaymentHandler}
              targetDate={getDate(new Date())}
            />
          </Grid>
        </Grid>
      </Box>
          
      <Typography id="confirm-amount">Note: It may take 2-3 business days before bank transfers are shown on your bank statement</Typography>
          
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} md={4}></Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Button color="inherit" onClick={() => closeCallback()} sx={{ width: "100%", marginLeft: 0 }} id="back-and-cancel-btn">
            Cancel
          </Button>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Button
            style={disableContinue || !isOTPEnabled ? {
              backgroundColor: "#D3D3D3",
              color: "#FFFFFF",
              width: "100%",
            } : {}}
            disabled={disableContinue || !isOTPEnabled}
            sx={{ width: "100%" }}
            onClick={handleContinue}
            variant="contained"
            id="payment-modal-btn"
            className={disableContinue || !isOTPEnabled ? "" : "active"} //it's for e2e test
          >
            Continue
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
